/**
 * Online lobby: szobanyitás 6 jegyű kóddal, csatlakozás kóddal, várakozó szoba
 * karakterválasztással, pálya/mód állítással és indítással.
 *
 * A hálózatot az OnlineClient intézi; ez a fájl csak DOM + lobby-szabályok.
 */
import './lobby.css';
import type { CharacterDef, OnlineMatchInfo, TrackDef } from '../core/types';
import { OnlineClient } from './client';
import type { OnlinePlayerView, OnlineRoomView } from './protocol';
import { portraitDataUrl } from '../render/portrait';

export interface OpenOnlineOptions {
  characters: CharacterDef[];
  tracks: TrackDef[];
  /** meglévő kapcsolat (visszatérés a szobába a verseny után) */
  client?: OnlineClient;
  initialCode?: string | null;
  mobile?: boolean;
}

export interface OnlineMatch {
  match: OnlineMatchInfo;
  client: OnlineClient;
}

type Screen = 'connecting' | 'home' | 'room';
type Tab = 'create' | 'join';

const NAME_KEY = 'csf.online.name';
const TRACK_KEY = 'csf.online.track';
const MAX_NAME = 14;

const make = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = '',
  text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

const button = (className: string, label: string): HTMLButtonElement => {
  const node = make('button', className, label);
  node.type = 'button';
  return node;
};

/** 6 jegyű kód kijelzése 3+3 bontásban, olvashatóbban. */
const formatCode = (code: string): string => `${code.slice(0, 3)} ${code.slice(3)}`;

const safeLocalStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback lentebb */
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
};

class Lobby {
  private readonly root: HTMLElement;
  private readonly options: OpenOnlineOptions;
  private readonly characters: CharacterDef[];
  private readonly tracks: TrackDef[];
  private readonly el: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly rttEl: HTMLSpanElement;
  private readonly client: OnlineClient;
  private readonly ownsClient: boolean;
  private readonly storage = safeLocalStorage();

  private screen: Screen = 'connecting';
  private tab: Tab = 'create';
  private room: OnlineRoomView | null = null;
  private name: string;
  private code = '';
  private trackId: string;
  private night = false;
  private ai = true;
  private busy = false;
  private message = '';
  private messageKind: 'info' | 'error' = 'info';
  private rttTimer = 0;
  private resolved = false;
  private resolvePromise: ((match: OnlineMatch | null) => void) | null = null;
  private readonly cleanups: (() => void)[] = [];

  constructor(root: HTMLElement, options: OpenOnlineOptions) {
    this.root = root;
    this.options = options;
    this.characters = options.characters.filter((c) => c.selectable).slice(0, 4);
    this.tracks = options.tracks;
    this.ownsClient = options.client === undefined;
    this.client = options.client ?? new OnlineClient();

    const savedName = this.storage?.getItem(NAME_KEY) ?? '';
    this.name = savedName || 'Játékos';
    const savedTrack = this.storage?.getItem(TRACK_KEY) ?? '';
    this.trackId = this.tracks.some((t) => t.id === savedTrack)
      ? savedTrack
      : (this.tracks[0]?.id ?? 'belvaros-reggel');
    this.code = (options.initialCode ?? '').toUpperCase().slice(0, 6);
    if (this.code.length === 6) this.tab = 'join';

    this.el = make('div', 'cso');
    this.el.tabIndex = -1;
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');
    this.el.setAttribute('aria-label', 'Online játék');

    this.panel = make('div', 'cso-panel');
    const header = make('header', 'cso-head');
    const brand = make('div', 'cso-brand');
    brand.append(make('span', 'cso-kicker', 'CSÚCSFORGALOM KUPA'), make('span', 'cso-online', 'ONLINE'));
    this.rttEl = make('span', 'cso-rtt', '— ms');
    const close = button('cso-close', '✕');
    close.setAttribute('aria-label', 'Bezárás');
    close.addEventListener('click', () => this.close());
    header.append(brand, this.rttEl, close);

    this.body = make('div', 'cso-body');
    this.panel.append(header, this.body);
    this.el.append(this.panel);

    this.el.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
    });
  }

  async open(): Promise<OnlineMatch | null> {
    this.root.appendChild(this.el);
    this.el.focus({ preventScroll: true });
    const promise = new Promise<OnlineMatch | null>((resolve) => {
      this.resolvePromise = resolve;
    });

    this.bindClient();
    this.rttTimer = window.setInterval(() => {
      this.rttEl.textContent = this.client.rtt > 0 ? `${Math.round(this.client.rtt)} ms` : '— ms';
    }, 800);

    if (this.client.connected && this.client.room) {
      this.room = this.client.room;
      this.screen = 'room';
      this.render();
    } else {
      this.render();
      try {
        await this.client.connect();
        if (this.resolved) return promise;
        this.screen = this.room ? 'room' : 'home';
        this.render();
      } catch (error) {
        if (this.resolved) return promise;
        this.screen = 'connecting';
        this.messageKind = 'error';
        this.message = (error as Error).message;
        this.render();
      }
    }
    return promise;
  }

  private bindClient(): void {
    this.cleanups.push(
      this.client.on('room', (msg) => {
        this.room = msg.room;
        this.busy = false;
        this.message = '';
        this.screen = 'room';
        this.render();
      }),
    );
    this.cleanups.push(
      this.client.on('start', (msg) => {
        if (this.resolved) return;
        this.resolved = true;
        this.stop();
        this.resolvePromise?.({ match: msg.match, client: this.client });
        this.resolvePromise = null;
      }),
    );
    this.cleanups.push(
      this.client.on('error', (msg) => {
        this.busy = false;
        this.messageKind = 'error';
        this.message = msg.message;
        this.render();
      }),
    );
    this.cleanups.push(
      this.client.on('closed', (msg) => {
        this.busy = false;
        this.room = null;
        this.screen = 'home';
        this.messageKind = 'error';
        this.message = msg.message || 'A szoba bezárt.';
        this.render();
      }),
    );
    this.cleanups.push(
      this.client.on('left-room', () => {
        this.room = null;
        this.screen = 'home';
        this.message = '';
        this.render();
      }),
    );
  }

  private close(): void {
    if (this.resolved) return;
    this.resolved = true;
    this.stop();
    if (this.ownsClient || this.room) this.client.leave();
    else this.client.close();
    this.resolvePromise?.(null);
    this.resolvePromise = null;
  }

  private stop(): void {
    window.clearInterval(this.rttTimer);
    this.rttTimer = 0;
    for (const cleanup of this.cleanups.splice(0)) cleanup();
    this.el.remove();
  }

  private render(): void {
    this.rttEl.textContent = this.client.rtt > 0 ? `${Math.round(this.client.rtt)} ms` : '— ms';
    this.body.replaceChildren();
    if (this.screen === 'connecting') this.renderConnecting();
    else if (this.screen === 'home') this.renderHome();
    else this.renderRoom();
  }

  private renderConnecting(): void {
    const wrap = make('section', 'cso-screen cso-connecting');
    wrap.append(
      make('span', 'cso-loading', '●'),
      make('h2', 'cso-title', 'KAPCSOLÓDÁS…'),
      make('p', 'cso-lead', 'A szoba-szerverhez csatlakozunk.'),
    );
    if (this.message) wrap.append(make('p', 'cso-msg is-error', this.message));
    const retry = button('cso-primary', 'ÚJRA');
    retry.addEventListener('click', () => {
      this.message = '';
      this.screen = 'connecting';
      this.render();
      void this.client
        .connect()
        .then(() => {
          this.screen = 'home';
          this.render();
        })
        .catch((error: Error) => {
          this.messageKind = 'error';
          this.message = error.message;
          this.render();
        });
    });
    wrap.append(retry);
    this.body.append(wrap);
  }

  private renderHome(): void {
    const wrap = make('section', 'cso-screen cso-home');
    const title = make('h2', 'cso-title', 'HOGY SZÁLLSZ BE?');
    const lead = make(
      'p',
      'cso-lead',
      'A gazda kódot kér, elküldi nektek, ti pedig beírjátok. Egy szobában max. 4 ember fér el – a maradék helyeket AI töltheti be.',
    );

    const nameRow = make('label', 'cso-field');
    nameRow.append(make('span', 'cso-label', 'A NEVED'));
    const nameInput = make('input', 'cso-input');
    nameInput.type = 'text';
    nameInput.maxLength = MAX_NAME;
    nameInput.autocomplete = 'off';
    nameInput.value = this.name;
    nameInput.addEventListener('input', () => {
      this.name = nameInput.value;
    });
    // az előző név legyen kijelölve, hogy gépelésre egyből cserélődjön
    nameInput.addEventListener('focus', () => nameInput.select());
    nameInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (this.tab === 'create') this.createRoom();
      else this.joinRoom();
    });
    nameRow.append(nameInput);

    const tabs = make('div', 'cso-tabs');
    const makeTab = (tab: Tab, label: string): void => {
      const node = button('cso-tab', label);
      if (this.tab === tab) node.classList.add('is-active');
      node.setAttribute('aria-pressed', this.tab === tab ? 'true' : 'false');
      node.addEventListener('click', () => {
        if (this.tab === tab) return;
        this.tab = tab;
        this.message = '';
        this.render();
      });
      tabs.append(node);
    };
    makeTab('create', 'SZOBANYITÁS');
    makeTab('join', 'CSATLAKOZÁS');

    wrap.append(title, lead, nameRow, tabs);
    if (this.tab === 'create') wrap.append(this.buildCreate());
    else wrap.append(this.buildJoin());
    if (this.message) wrap.append(make('p', `cso-msg is-${this.messageKind}`, this.message));
    this.body.append(wrap);
  }

  private buildCreate(): HTMLElement {
    const box = make('div', 'cso-create');
    box.append(make('span', 'cso-label', 'PÁLYA'));

    const grid = make('div', 'cso-tracks');
    for (const track of this.tracks) {
      const card = button('cso-track', '');
      if (track.id === this.trackId) card.classList.add('is-active');
      card.setAttribute('aria-pressed', track.id === this.trackId ? 'true' : 'false');
      card.append(
        make('span', 'cso-track-name', track.name),
        make('span', 'cso-track-sub', track.subtitle),
      );
      card.addEventListener('click', () => {
        this.trackId = track.id;
        this.render();
      });
      grid.append(card);
    }

    const toggles = make('div', 'cso-toggles');
    const nightBtn = button('cso-toggle', `ÉJSZAKA: ${this.night ? 'BE' : 'KI'}`);
    nightBtn.classList.toggle('is-on', this.night);
    nightBtn.setAttribute('aria-pressed', this.night ? 'true' : 'false');
    nightBtn.addEventListener('click', () => {
      this.night = !this.night;
      this.render();
    });
    const aiBtn = button('cso-toggle', `AI ELLENFELEK: ${this.ai ? 'BE' : 'KI'}`);
    aiBtn.classList.toggle('is-on', this.ai);
    aiBtn.setAttribute('aria-pressed', this.ai ? 'true' : 'false');
    aiBtn.addEventListener('click', () => {
      this.ai = !this.ai;
      this.render();
    });
    toggles.append(nightBtn, aiBtn);

    const create = button('cso-primary cso-submit', this.busy ? 'SZOBANYITÁS…' : 'SZOBANYITÁS');
    create.disabled = this.busy;
    create.addEventListener('click', () => this.createRoom());

    const hint = make(
      'p',
      'cso-hint',
      this.ai
        ? '6 kart a pályán: a 4 ember mellé 2 AI kerül. Indíthatsz 1 emberrel is.'
        : 'AI nélkül csak az emberek versenyeznek – 1 emberrel is indítható, de akkor egyedül körözöl.',
    );

    box.append(grid, toggles, create, hint);
    return box;
  }

  private buildJoin(): HTMLElement {
    const box = make('div', 'cso-join');
    box.append(make('span', 'cso-label', 'SZOB KÓD'));
    const codeInput = make('input', 'cso-input cso-code');
    codeInput.type = 'text';
    codeInput.inputMode = 'text';
    codeInput.autocomplete = 'off';
    codeInput.spellcheck = false;
    codeInput.maxLength = 6;
    codeInput.placeholder = 'ABC123';
    codeInput.value = this.code;
    codeInput.addEventListener('input', () => {
      this.code = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
      codeInput.value = this.code;
    });
    codeInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.joinRoom();
    });
    const join = button('cso-primary cso-submit', this.busy ? 'CSATLAKOZÁS…' : 'CSATLAKOZÁS');
    join.disabled = this.busy;
    join.addEventListener('click', () => this.joinRoom());
    const hint = make('p', 'cso-hint', 'A kód 6 betű/szám. Nagybetűvel is elég beírni.');
    box.append(codeInput, join, hint);
    window.setTimeout(() => codeInput.focus(), 30);
    return box;
  }

  private createRoom(): void {
    if (this.busy) return;
    const name = this.name.trim();
    if (!name) {
      this.messageKind = 'error';
      this.message = 'Adj meg egy nevet.';
      this.render();
      return;
    }
    this.persistName(name);
    this.storage?.setItem(TRACK_KEY, this.trackId);
    this.busy = true;
    this.message = '';
    this.render();
    this.client.create(name, { trackId: this.trackId, night: this.night, ai: this.ai, laps: 3 });
  }

  private joinRoom(): void {
    if (this.busy) return;
    const name = this.name.trim();
    if (!name) {
      this.messageKind = 'error';
      this.message = 'Adj meg egy nevet.';
      this.render();
      return;
    }
    if (this.code.length !== 6) {
      this.messageKind = 'error';
      this.message = 'A kód 6 karakter. Kérd el újra a gazdától.';
      this.render();
      return;
    }
    this.persistName(name);
    this.busy = true;
    this.message = '';
    this.render();
    this.client.join(this.code, name);
  }

  private persistName(name: string): void {
    this.name = name;
    this.storage?.setItem(NAME_KEY, name);
  }

  private renderRoom(): void {
    const room = this.room;
    if (!room) {
      this.screen = 'home';
      this.render();
      return;
    }
    const wrap = make('section', 'cso-screen cso-room');

    const codeRow = make('div', 'cso-code-row');
    const codeBox = make('div', 'cso-code-box');
    codeBox.append(make('span', 'cso-label', 'SZOB KÓD'), make('strong', 'cso-code-big', formatCode(room.code)));
    const copy = button('cso-small', 'KÓD MÁSOLÁSA');
    copy.addEventListener('click', () => void this.flashCopy(copy, room.code));
    const link = button('cso-small', 'LINK MÁSOLÁSA');
    link.addEventListener('click', () => {
      const url = `${window.location.origin}${window.location.pathname}?join=${room.code}`;
      void this.flashCopy(link, url);
    });
    codeBox.append(copy, link);
    const phase = make(
      'span',
      'cso-phase',
      room.phase === 'results' ? 'EREDMÉNY' : 'VÁRAKOZÁS',
    );
    codeRow.append(codeBox, phase);

    const players = make('div', 'cso-players');
    const seats: (OnlinePlayerView | null)[] = [];
    for (let slot = 0; slot < room.maxPlayers; slot++) {
      seats.push(room.players.find((p) => p.slot === slot) ?? null);
    }
    for (let slot = 0; slot < room.maxPlayers; slot++) {
      players.append(this.buildSeat(slot, seats[slot], room));
    }

    const chars = make('div', 'cso-chars');
    for (const character of this.characters) {
      chars.append(this.buildCharacterCard(character, room));
    }

    wrap.append(
      codeRow,
      make('p', 'cso-hint cso-room-hint', 'A gazda indíthat, amikor akar. Indítás után már nem lehet becsatlakozni.'),
      players,
      make('span', 'cso-label', 'KARAKTER'),
      chars,
    );

    const settings = this.buildRoomSettings(room);
    wrap.append(settings);

    const footer = make('footer', 'cso-footer');
    const me = room.players.find((p) => p.id === room.you.id);
    const ready = room.players.every((p) => p.character);
    const status = make('div', 'cso-status');
    if (room.phase === 'results') {
      status.append(make('strong', '', 'A verseny véget ért.'), make('span', '', 'A gazda indíthat új futamot.'));
    } else if (room.you.host) {
      status.append(
        make('strong', '', `${room.players.length}/${room.maxPlayers} ember a szobában`),
        make('span', '', ready ? 'Mindenki választott karaktert.' : 'Aki nem választ, annak induláskor automatikusan jut karakter.'),
      );
    } else {
      status.append(
        make('strong', '', me?.character ? 'Készen állsz.' : 'Válassz karaktert!'),
        make('span', '', 'A gazda indítja a versenyt.'),
      );
    }
    footer.append(status);

    if (room.you.host) {
      const start = button('cso-primary cso-start', room.phase === 'results' ? 'ÚJ FUTAM' : 'VERSENY INDÍTÁSA');
      start.disabled = room.phase !== 'lobby' || room.players.length === 0;
      start.addEventListener('click', () => this.startRace(room));
      footer.append(start);
    } else {
      footer.append(make('span', 'cso-wait', room.phase === 'results' ? 'Várakozás a gazdára…' : 'A gazda indít…'));
    }
    wrap.append(footer);

    if (this.message) wrap.append(make('p', `cso-msg is-${this.messageKind}`, this.message));
    this.body.append(wrap);
  }

  private buildSeat(slot: number, player: OnlinePlayerView | null, room: OnlineRoomView): HTMLElement {
    const seat = make('div', 'cso-seat');
    seat.append(make('span', 'cso-seat-no', `${slot + 1}`));
    if (!player) {
      seat.classList.add('is-empty');
      seat.append(make('span', 'cso-seat-name', 'szabad hely'));
      return seat;
    }
    const character = this.characters.find((c) => c.id === player.character);
    if (character) {
      const img = make('img', 'cso-seat-portrait');
      img.src = portrait(character);
      img.alt = '';
      img.width = 34;
      img.height = 34;
      seat.append(img);
    } else {
      seat.append(make('span', 'cso-seat-blank', '?'));
    }
    const info = make('div', 'cso-seat-info');
    const line = make('span', 'cso-seat-name', player.name);
    if (player.host) line.append(make('span', 'cso-badge', 'GAZDA'));
    if (player.id === room.you.id) line.append(make('span', 'cso-badge is-you', 'TE'));
    info.append(line, make('span', 'cso-seat-char', character?.name ?? 'nincs karakter'));
    seat.append(info);
    return seat;
  }

  private buildCharacterCard(character: CharacterDef, room: OnlineRoomView): HTMLElement {
    const mine = room.players.find((p) => p.id === room.you.id);
    const owner = room.players.find((p) => p.character === character.id);
    const isMine = owner?.id === room.you.id;
    const taken = owner !== undefined && !isMine;
    const card = button('cso-char', '');
    if (isMine) card.classList.add('is-mine');
    if (taken) card.classList.add('is-taken');
    card.disabled = taken || room.phase !== 'lobby' || isMine;
    const img = make('img', 'cso-char-portrait');
    img.src = portrait(character);
    img.alt = '';
    img.width = 62;
    img.height = 62;
    const name = make('span', 'cso-char-name', character.name);
    const note = make('span', 'cso-char-note', isMine ? 'a te kocsid' : taken ? (owner?.name ?? 'foglalt') : 'szabad');
    card.append(img, name, note);
    card.addEventListener('click', () => {
      if (taken || isMine || room.phase !== 'lobby') return;
      this.client.pick(character.id);
    });
    if (!mine?.character && !taken && room.phase === 'lobby') card.classList.add('is-suggested');
    return card;
  }

  private buildRoomSettings(room: OnlineRoomView): HTMLElement {
    const box = make('div', 'cso-room-settings');
    const label = make('span', 'cso-label', 'BEÁLLÍTÁSOK');
    box.append(label);
    const row = make('div', 'cso-settings-row');
    for (const track of this.tracks) {
      const chip = button('cso-chip', track.name);
      if (track.id === room.settings.trackId) chip.classList.add('is-active');
      chip.disabled = !room.you.host || room.phase !== 'lobby';
      chip.addEventListener('click', () => {
        if (!room.you.host || room.phase !== 'lobby') return;
        this.client.updateSettings({ trackId: track.id });
      });
      row.append(chip);
    }
    box.append(row);

    const toggles = make('div', 'cso-settings-row');
    const night = button('cso-chip', `ÉJSZAKA: ${room.settings.night ? 'BE' : 'KI'}`);
    night.classList.toggle('is-on', room.settings.night);
    night.disabled = !room.you.host || room.phase !== 'lobby';
    night.addEventListener('click', () => {
      if (!room.you.host || room.phase !== 'lobby') return;
      this.client.updateSettings({ night: !room.settings.night });
    });
    const ai = button('cso-chip', `AI: ${room.settings.ai ? 'BE' : 'KI'}`);
    ai.classList.toggle('is-on', room.settings.ai);
    ai.disabled = !room.you.host || room.phase !== 'lobby';
    ai.addEventListener('click', () => {
      if (!room.you.host || room.phase !== 'lobby') return;
      this.client.updateSettings({ ai: !room.settings.ai });
    });
    toggles.append(night, ai);
    box.append(toggles);
    return box;
  }

  private startRace(room: OnlineRoomView): void {
    const used = new Set(room.players.map((p) => p.character).filter((id): id is string => Boolean(id)));
    const free = this.characters.map((c) => c.id).filter((id) => !used.has(id));
    const picks: string[] = [];
    for (const player of [...room.players].sort((a, b) => a.slot - b.slot)) {
      const own = player.character;
      if (own && !picks.includes(own)) {
        picks.push(own);
        continue;
      }
      const fallback = free.shift() ?? this.characters[0]?.id;
      if (!fallback) return;
      picks.push(fallback);
      used.add(fallback);
    }
    this.message = '';
    this.client.start(picks);
  }

  private async flashCopy(target: HTMLButtonElement, text: string): Promise<void> {
    const ok = await copyText(text);
    const original = target.textContent ?? '';
    target.textContent = ok ? 'MÁSOLVA!' : 'NEM SIKERÜLT';
    window.setTimeout(() => {
      target.textContent = original;
    }, 1200);
  }
}

const portraitCache = new Map<string, string>();
const portrait = (character: CharacterDef): string => {
  const cached = portraitCache.get(character.id);
  if (cached) return cached;
  const url = portraitDataUrl(character, 128);
  portraitCache.set(character.id, url);
  return url;
};

export async function openOnlineLobby(
  root: HTMLElement,
  options: OpenOnlineOptions,
): Promise<OnlineMatch | null> {
  const lobby = new Lobby(root, options);
  return lobby.open();
}
